import React, { useState, useRef } from 'react'
import { ShieldCheck, CheckCircle, Loader2, Lock, BadgeCheck } from 'lucide-react'
import { Button } from '../ui/Button'

export type CertifIDState = 'idle' | 'verifying' | 'verified'

export interface CertifIDResult {
  identityChecked: boolean
  bankAccountOwnershipConfirmed: boolean
  insuredAmount: string
  insuredBy: string
  verifiedAt: string
}

function simulateCertifIDVerification(): Promise<CertifIDResult> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        identityChecked: true,
        bankAccountOwnershipConfirmed: true,
        insuredAmount: '$5,000,000',
        insuredBy: "Lloyd's of London",
        verifiedAt: new Date().toISOString(),
      })
    }, 2200)
  })
}

interface CertifIDVerificationProps {
  context: 'escrow-inbound' | 'disbursement-outbound'
  onVerified?: (result: CertifIDResult) => void
}

const LABEL_BY_CONTEXT: Record<CertifIDVerificationProps['context'], string> = {
  'escrow-inbound': 'Verify originator wire instructions',
  'disbursement-outbound': 'Verify payee bank account',
}

const CHECK_LABELS_BY_CONTEXT: Record<CertifIDVerificationProps['context'], { identity: string; ownership: string }> = {
  'escrow-inbound': {
    identity: 'Originator identity check',
    ownership: 'Bank-account ownership confirmed',
  },
  'disbursement-outbound': {
    identity: 'Payee identity check',
    ownership: 'Bank-account ownership confirmed',
  },
}

export const CertifIDVerification: React.FC<CertifIDVerificationProps> = ({
  context,
  onVerified,
}) => {
  const [state, setState] = useState<CertifIDState>('idle')
  const [result, setResult] = useState<CertifIDResult | null>(null)
  const verifyRef = useRef<ReturnType<typeof simulateCertifIDVerification> | null>(null)

  function handleVerify() {
    if (state !== 'idle') return
    setState('verifying')
    verifyRef.current = simulateCertifIDVerification()
    verifyRef.current.then((verified) => {
      setResult(verified)
      setState('verified')
      onVerified?.(verified)
    })
  }

  const checkLabels = CHECK_LABELS_BY_CONTEXT[context]
  const buttonLabel = LABEL_BY_CONTEXT[context]

  if (state === 'idle') {
    return (
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-9 w-9 rounded-full bg-blue-50 dark:bg-blue-900/30 flex-shrink-0">
              <ShieldCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                CertifID Wire Fraud Protection
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Optional — verify before proceeding
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleVerify}
            className="shrink-0 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            aria-label={`Verify with CertifID: ${buttonLabel}`}
          >
            <ShieldCheck className="h-4 w-4 mr-1.5" />
            Verify with CertifID
          </Button>
        </div>
      </div>
    )
  }

  if (state === 'verifying') {
    return (
      <div
        className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/10 p-4"
        role="status"
        aria-label="CertifID verification in progress"
      >
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Verifying with CertifID…
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Checking identity and bank-account ownership
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 p-4 space-y-3"
      aria-label="CertifID verification complete"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Verified by CertifID
          </p>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-semibold">
          <BadgeCheck className="h-3.5 w-3.5" />
          Verified
        </span>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
          <span className="text-slate-700 dark:text-slate-300">{checkLabels.identity}</span>
          <span className="ml-auto text-green-600 dark:text-green-400 text-xs font-semibold">Passed</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
          <span className="text-slate-700 dark:text-slate-300">{checkLabels.ownership}</span>
          <span className="ml-auto text-green-600 dark:text-green-400 text-xs font-semibold">Confirmed</span>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1 border-t border-blue-100 dark:border-blue-800">
        <Lock className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
        <p className="text-xs text-slate-600 dark:text-slate-400">
          Insured wire up to {result?.insuredAmount ?? '$5,000,000'}
          {' '}&mdash;{' '}
          {result?.insuredBy ?? "Lloyd's of London"}
        </p>
      </div>
    </div>
  )
}
